import React, { useContext, useEffect, useMemo, useState } from "react";
import { HolderOutlined, DeleteOutlined } from "@ant-design/icons";
import type { DragEndEvent } from "@dnd-kit/core";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  Col,
  Row,
  Input,
  InputNumber,
  Checkbox,
  Collapse,
  Form,
  FormInstance,
  Switch,
  Empty,
  Modal,
} from "antd";
import type { ColProps } from "antd/es/grid/col";
import { useTranslation } from "react-i18next";
import { IoMdAddCircle } from "react-icons/io";
import {
  AppDispatch,
  dynamicSet,
  getClientOrganization,
  useDynamicSelector,
} from "@src/redux";
import { useDispatch, useSelector } from "react-redux";

interface DataType {
  id?: string;
  key: string;
  name: string;
  isMandatory: boolean; // Assuming "mandatory" is a number
  priorityOrder?: number;
}

interface props {
  form: FormInstance;
  setSubjectKey: any;
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: any;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface SortableRowProps {
  "data-row-key": string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const SortableRow: React.FC<SortableRowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <Row
        ref={setNodeRef}
        style={style}
        {...attributes}
        align="middle"
        gutter={16}
      >
        {props.children}
      </Row>
    </RowContext.Provider>
  );
};

const SubjectKeyList: React.FC<props> = (props) => {
  const { form, setSubjectKey } = props;
  // const [dataSource, setDataSource] = useState<DataType[]>([]);
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { result: getClientOrganizationResult } = useSelector(
    getClientOrganization
  );
  const { removableSubjectKeyIdList: removableSubjectKeyIdList } =
    useDynamicSelector("removable_subject_key_id_list");

  const [keyDeleteModal, setKeyDeleteModal] = useState<boolean>(false);
  const [deleteItem, setDeleteItem] = useState<{
    key: string;
    id?: string;
    name?: string;
  } | null>(null);
  const [dataSource, setDataSource] = useState<DataType[]>([
    {
      key: "1",
      name: "",
      isMandatory: false,
    },
  ]);
  const handleAdd = () => {
    const newKey = (dataSource.length + 1).toString();
    const newRow: DataType = {
      key: newKey,
      name: ``,
      isMandatory: false,
    };
    setDataSource((prev) => [...prev, newRow]);
  };

  const handleChange = (key: string, field: keyof DataType, value: any) => {
    setDataSource((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, [field]: value } : item
      )
    );
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.key === active.id
        );
        const overIndex = prevState.findIndex(
          (record) => record.key === over?.id
        );
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
  };
  useEffect(() => {
    if (dataSource.length > 0) {
      setSubjectKey(dataSource);
    } else {
      setSubjectKey([]);
    }
  }, [dataSource]);
  useEffect(() => {
    if (
      getClientOrganizationResult?.subjectKeyList &&
      getClientOrganizationResult?.subjectKeyList?.length > 0
    ) {
      let reGenerate = getClientOrganizationResult?.subjectKeyList.map(
        (list: any, index: number) => {
          return {
            ...list,
            id: list.id,
            key: index.toString(),
          };
        }
      );

      setDataSource(reGenerate);
    }
  }, [getClientOrganizationResult]);

  const handleClose = () => {
    setKeyDeleteModal(false);
  };

  const handleRemove = (key: string, id?: string, name?: string) => {
    setDeleteItem({ key, id, name }); // Store the item to be deleted
    setKeyDeleteModal(true); // Open confirmation modal
  };

  const handleConfirmRemove = () => {
    if (deleteItem) {
      dispatch(
        dynamicSet("removable_subject_key_id_list", {
          removableSubjectKeyIdList:
            removableSubjectKeyIdList?.length > 0
              ? [...removableSubjectKeyIdList, deleteItem.id]
              : [deleteItem.id],
        })
      );
      setDataSource((prev) =>
        prev.filter((item) => item.key !== deleteItem.key)
      );
      setKeyDeleteModal(false); // Close modal
    }
  };

  return (
    <div>
      {/* <Collapse>
        <Collapse.Panel
          header={ */}
      <div className="level-title-container">
        <h4>{t("subject_keys")}</h4>
      </div>
      {/* }
          key={0}
          showArrow={false}
          > */}
      <div className="level-list-container">
        <Row
          gutter={10}
          style={{ marginLeft: "20px" }}
          align={"bottom"}
          justify={"space-between"}
        >
          <Col span={8}>
            <Form.Item
              name={"subjectLabel"}
              label={t("subjectLabel")}
              rules={[
                { required: true, message: t("error:label_is_required") },
              ]}
              style={{ marginBottom: "0px" }}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={"hasSubjectLogin"}
              label={t("allow_subject_login")}
              rules={[
                {
                  required: false,
                  message: t("error:allow_subject_login_is_required"),
                },
              ]}
              style={{ marginBottom: "0px" }}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>

          <div className="client_key_container">
            <div className="header_container">
              <span className="header-text">{t("keys")}</span>
              <Button onClick={handleAdd} icon={<IoMdAddCircle size={20} />} />
            </div>

            {dataSource.length > 0 ? (
              <>
                <div className="sub-question-container">
                  <div className="sub-question-sno-label">
                    <span>{t("s_no")}</span>
                  </div>
                  <div className="sub-question-name-label">
                    <span>{t("name")}</span>
                  </div>
                  <div className="sub-question-mandatory-label">
                    <span>{t("mandatory")}</span>
                  </div>
                  <div className="sub-question-column-container"></div>
                </div>
                <DndContext
                  modifiers={[restrictToVerticalAxis]}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext
                    items={dataSource.map((item) => item.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {dataSource.map((record, index) => (
                      <SortableRow key={record.key} data-row-key={record.key}>
                        <Row
                          gutter={24}
                          justify={"space-between"}
                          style={{
                            marginBottom: "10px",
                            width: "80%",
                            padding: "10px",
                            marginLeft: "20px",
                            backgroundColor: "white",
                            borderRadius: "10px",
                          }}
                          align={"middle"}
                        >
                          <Col span={2}>
                            <DragHandle />
                          </Col>
                          <Col span={2}>
                            <b className="">{index + 1}</b>
                          </Col>
                          <Col span={10}>
                            <div className="column-container">
                              {/* <span>{t("name")}</span> */}
                              {/* <Form.Item
                            name={["subject_keys", index, "name"]}
                            style={{ marginBottom: "0px" }}
                          > */}
                              <Input
                                value={record.name}
                                onChange={(e) =>
                                  handleChange(
                                    record.key,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />
                              {/* </Form.Item>/ */}
                            </div>
                          </Col>
                          <Col span={5}>
                            <div className="column-container">
                              {/* <span>{t("mandatory")}</span> */}
                              {/* <Form.Item
                            name={["subject_keys", index, "isMandatory"]}
                            valuePropName="checked"
                            style={{ marginBottom: "0px" }}
                          > */}
                              <Checkbox
                                value={record?.isMandatory}
                                checked={record?.isMandatory || false}
                                onChange={(e) =>
                                  handleChange(
                                    record.key,
                                    "isMandatory",
                                    e.target.checked
                                  )
                                }
                              />
                              {/* </Form.Item> */}
                            </div>
                          </Col>
                          <Col span={2} className="button-column">
                            {dataSource.length > 1 &&
                            dataSource.findIndex(
                              (item) => item.key === record.key
                            ) !== 0 ? (
                              <Button
                                type="text"
                                icon={
                                  <DeleteOutlined className="delete-icon" />
                                }
                                onClick={() =>
                                  handleRemove(
                                    record.key,
                                    record.id,
                                    record.name
                                  )
                                }
                              />
                            ) : null}
                          </Col>
                        </Row>
                      </SortableRow>
                    ))}
                  </SortableContext>
                </DndContext>
              </>
            ) : (
              <Empty description={t("no_subject_keys")} />
            )}
          </div>
        </Row>
        {/* <Row
          gutter={24}
          justify={"space-between"}
          style={{
            marginBottom: "10px",
            width: "80%",
            padding: "20px",
            marginLeft: "20px",
          }}
          align={"middle"}
        >
          <div className="column-container">
            <span>{t("s_no")}</span>
          </div>
          <div className="column-container">
            <span>{t("name")}</span>
          </div>
          <div className="column-container">
            <span>{t("mandatory")}</span>
          </div>
        </Row> */}
      </div>
      {/* </Collapse.Panel>
      </Collapse> */}

      <Modal
        open={keyDeleteModal}
        title={t("key_delete_confirmation")}
        onCancel={handleClose}
        onOk={handleConfirmRemove}
      >
        {t("key_delete_content")} <b>{deleteItem?.name}</b>
      </Modal>
    </div>
  );
};

export default SubjectKeyList;
